/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {useProduct, useProducts, usePromotions} from '@salesforce/commerce-sdk-react';
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant';
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover';
import {Box, Flex, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {getDisplayVariationValues} from '@salesforce/retail-react-app/app/utils/product-utils';

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, currency, ...props}) => {
  const variant = useItemVariant();
  const {data: basket} = useCurrentBasket();
  const {currency: activeCurrency} = useCurrency();
  const promotionIds = variant.priceAdjustments?.map((adj) => adj.promotionId) ?? [];
  const intl = useIntl();

  // Fetch all the promotions given by price adjustments. We display this info in
  // the promotion info popover when applicable.
  const {data: res} = usePromotions(
    {
      parameters: {
        ids: promotionIds.join(','),
      },
    },
    {
      enabled: promotionIds.length > 0,
    }
  );
  const promos = res?.data || [];
  const variationValues = getDisplayVariationValues(
    variant?.variationAttributes,
    variant?.variationValues
  );

  // get variant info for bundled products in cart page and order history page
  const productBundleIds =
    variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? '';
  const {data: productBundleVariantData, isLoading: bundleVariantIsLoading} = useProducts(
    {
      parameters: {
        ids: productBundleIds,
        allImages: false,
      },
    },
    {
      enabled: Boolean(variant?.type?.bundle && productBundleIds),
      select: (result) => {
        // formats response so we can easily display child quantity/variant selection
        return result?.data?.map((item) => {
          const quantity = variant?.bundledProductItems.find(
            (childProduct) => childProduct.productId === item.id
          )?.quantity;

          return {
            ...item,
            quantity,
            variationValues: getDisplayVariationValues(
              item?.variationAttributes,
              item?.variationValues
            ),
          };
        });
      },
    }
  );

  // get bundle product data for wishlist page
  const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
    {
      parameters: {
        id: variant?.id,
        allImages: false,
      },
    },
    {
      enabled: Boolean(variant?.type?.bundle && !productBundleIds),
    }
  );

  return (
    <Stack flex={1} spacing={1.5} {...props}>
      {variationValues &&
        Object.keys(variationValues).map((key) => (
          <Text
            key={`${key}: ${variationValues[key]}`}
            color="gray.700"
            fontSize="sm"
            lineHeight={1}
          >
            {key}: {variationValues[key]}
          </Text>
        ))}

      {includeQuantity && (
        <Text color="gray.700" fontSize="sm" lineHeight={1}>
          <FormattedMessage
            defaultMessage="Quantity: {quantity}"
            id="item_attributes.label.quantity"
            values={{quantity: variant.quantity}}
          />
        </Text>
      )}

      {!productBundleIsLoading && productBundleData && !productBundleVariantData && (
        <Box>
          {productBundleData?.bundledProducts.map(({product, quantity}) => (
            <Box key={product.id} marginTop={2}>
              <Text as="b" color="gray.700" fontSize="sm">
                {product?.name}
              </Text>
              <Text color="gray.700" fontSize="sm">
                {intl.formatMessage({
                  defaultMessage: 'Qty',
                  id: 'add_to_cart_modal.label.quantity',
                })}
                : {quantity}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {!bundleVariantIsLoading && productBundleVariantData && (
        <Box>
          <Text fontSize={15} fontWeight={500} marginTop={3}>
            {intl.formatMessage({
              defaultMessage: 'Selected Options',
              id: 'item_attributes.label.selected_options',
            })}
            :
          </Text>
          {productBundleVariantData?.map(
            ({variationValues, name: productName, quantity, id}) => {
              return (
                <Box key={id} marginTop={2}>
                  <Text as="b" color="gray.700" fontSize="sm">
                    {productName}
                  </Text>
                  <Text color="gray.700" fontSize="sm">
                    {intl.formatMessage({
                      defaultMessage: 'Qty',
                      id: 'add_to_cart_modal.label.quantity',
                    })}
                    : {quantity}
                  </Text>
                  {Object.keys(variationValues).map((key) => {
                    const selectedVariant = `${key}: ${variationValues[key]}`;

                    return (
                      <Text
                        key={selectedVariant}
                        color="gray.700"
                        fontSize="sm"
                      >
                        {selectedVariant}
                      </Text>
                    );
                  })}
                </Box>
              );
            }
          )}
        </Box>
      )}

      {variant.priceAdjustments?.length > 0 && (
        <Flex alignItems="center">
          <Text color="gray.700" fontSize="sm" lineHeight={1}>
            <FormattedMessage
              defaultMessage="Promotions"
              id="item_attributes.label.promotions"
            />
            {': '}
            <Text as="span" color="green.700">
              <FormattedNumber
                currency={currency || basket?.currency || activeCurrency}
                style="currency"
                value={variant.priceAdjustments.reduce(
                  (acc, adj) => acc + (adj.price ?? 0),
                  0
                )}
              />
            </Text>
          </Text>
          <PromoPopover ml={2}>
            <Stack>
              {promos?.map((promo) => (
                <Text key={promo?.id} fontSize="sm">
                  {promo?.calloutMsg}
                </Text>
              ))}
            </Stack>
          </PromoPopover>
        </Flex>
      )}
    </Stack>
  );
};

ItemAttributes.propTypes = {
  includeQuantity: PropTypes.bool,
  currency: PropTypes.string,
};

export default ItemAttributes;
